import { createAsyncThunk } from "@reduxjs/toolkit";
import { postLoginService } from "../../Services";
import { alert } from "../../utils/commonFunctions/alert";

const normalizeNumericIdArray = (values) => {
    if (!Array.isArray(values)) return [];
    return values
        .map((item) => {
            const raw = typeof item === "object" && item !== null ? item.id : item;
            const parsed = Number(raw);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        })
        .filter((id) => id !== null);
};

export const fetchDealTypes = createAsyncThunk(
    "deals/fetchDealTypes",
    async () => {
        try {
            const url = `/api/deal-types`;
            const response = await postLoginService.get(url);
            // console.log("FETCH DEAL TYPES RESPONSE---->", response);

            if (response?.success) {
                return response;
            } else {
                alert.error(response?.message || "Failed to fetch deal types");
                throw { message: response?.message || "Failed to fetch deal types" };
            }
        } catch (error) {
            const errorMessage = error?.message || "Failed to fetch deal types";
            alert.error(errorMessage);
            throw error;
        }
    }
);

export const fetchCategories = createAsyncThunk(
    "deals/fetchCategories",
    async () => {
        try {
            const url = `/api/categories`;
            const response = await postLoginService.get(url);
            // console.log("FETCH CATEGORIES RESPONSE---->", response);

            if (response?.success) {
                return response;
            } else {
                alert.error(response?.message || "Failed to fetch categories");
                throw { message: response?.message || "Failed to fetch categories" };
            }
        } catch (error) {
            const errorMessage = error?.message || "Failed to fetch categories";
            alert.error(errorMessage);
            throw error;
        }
    }
);

export const fetchDealTerms = createAsyncThunk(
    "deals/fetchDealTerms",
    async () => {
        try {
            const url = `/api/deal-terms/merchant`;
            const response = await postLoginService.get(url);
            // console.log("FETCH DEAL TERMS RESPONSE---->", response);

            if (response?.success) {
                return response;
            } else {
                alert.error(response?.message || "Failed to fetch deal terms");
                throw { message: response?.message || "Failed to fetch deal terms" };
            }
        } catch (error) {
            const errorMessage = error?.message || "Failed to fetch deal terms";
            alert.error(errorMessage);
            throw error;
        }
    }
);

export const createDealTerm = createAsyncThunk(
    "deals/createDealTerm",
    async (term, { dispatch }) => {
        try {
            const url = `/api/deal-terms/merchant`;
            const payload = {
                term: term,
                isActive: true,
                isGeneral: false,
                sortOrder: 1
            };
            const response = await postLoginService.post(url, payload);
            // console.log("CREATE DEAL TERM RESPONSE---->", response);

            if (response?.success) {
                // alert.success(response?.message || "Term created successfully");
                // Refresh the deal terms list after creating
                dispatch(fetchDealTerms());
                return response;
            } else {
                alert.error(response?.message || "Failed to create term");
                throw { message: response?.message || "Failed to create term" };
            }
        } catch (error) {
            const errorMessage = error?.message || "Failed to create term";
            alert.error(errorMessage);
            throw error;
        }
    }
);

export const updateDealTerm = createAsyncThunk(
    "deals/updateDealTerm",
    async ({ id, term }, { dispatch }) => {
        try {
            const url = `/api/deal-terms/merchant/${id}`;
            const payload = {
                term: term,
                isActive: true,
                sortOrder: 1
            };
            const response = await postLoginService.put(url, payload);
            // console.log("UPDATE DEAL TERM RESPONSE---->", response);

            if (response?.success) {
                // alert.success(response?.message || "Term updated successfully");
                // Refresh the deal terms list after updating
                dispatch(fetchDealTerms());
                return response;
            } else {
                alert.error(response?.message || "Failed to update term");
                throw { message: response?.message || "Failed to update term" };
            }
        } catch (error) {
            const errorMessage = error?.message || "Failed to update term";
            alert.error(errorMessage);
            throw error;
        }
    }
);

export const deleteDealTerm = createAsyncThunk(
    "deals/deleteDealTerm",
    async (id, { dispatch }) => {
        try {
            const url = `/api/deal-terms/merchant/${id}`;
            const response = await postLoginService.delete(url);
            // console.log("DELETE DEAL TERM RESPONSE---->", response);

            if (response?.success) {
                // alert.success(response?.message || "Term deleted successfully");
                // Refresh the deal terms list after deleting
                dispatch(fetchDealTerms());
                return response;
            } else {
                alert.error(response?.message || "Failed to delete term");
                throw { message: response?.message || "Failed to delete term" };
            }
        } catch (error) {
            const errorMessage = error?.message || "Failed to delete term";
            alert.error(errorMessage);
            throw error;
        }
    }
);

export const deleteDeal = createAsyncThunk(
    "deals/deleteDeal",
    async (dealId) => {
        try {
            const url = `/api/merchants/deals/${dealId}`;
            const response = await postLoginService.delete(url);
            // console.log("DELETE DEAL RESPONSE---->", response);

            if (response?.success) {
                // alert.success(response?.message || "Deal deleted successfully");
                return { ...response, dealId };
            } else {
                alert.error(response?.message || "Failed to delete deal");
                throw { message: response?.message || "Failed to delete deal" };
            }
        } catch (error) {
            const errorMessage = error?.message || "Failed to delete deal";
            alert.error(errorMessage);
            throw error;
        }
    }
);

// Update an existing deal
export const updateDeal = createAsyncThunk(
    "deals/updateDeal",
    async ({ dealId, dealData, imageChanged, restrictToLockedFields = false }, { rejectWithValue }) => {
        try {
            const url = `/api/merchants/deals/${dealId}`;

            // Create FormData for file upload
            const formData = new FormData();

            const appendIfDefined = (key, value) => {
                if (value === undefined || value === null || value === "") return;
                formData.append(key, value);
            };

            const appendBooleanIfDefined = (key, value) => {
                if (value === undefined || value === null) return;
                formData.append(key, value ? "true" : "false");
            };

            if (restrictToLockedFields) {
                if (Array.isArray(dealData.categoryIds)) {
                    const normalizedCategoryIds = normalizeNumericIdArray(dealData.categoryIds);
                    formData.append("categoryIds", JSON.stringify(normalizedCategoryIds));
                }

                appendIfDefined("totalQuantity", dealData.totalQuantity);

                if (dealData.image instanceof File) {
                    formData.append("image", dealData.image);
                }

                if (imageChanged && !dealData.image && dealData.existingImageUrl) {
                    formData.append("clearImage", "true");
                }

                if (dealData.status) {
                    formData.append("status", dealData.status);
                }
            } else {
                // Add text fields
                appendIfDefined("title", dealData.title);
                appendIfDefined("description", dealData.description);
                if (dealData.kitchenDescription && dealData.kitchenDescription.trim())
                    appendIfDefined("kitchenDescription", dealData.kitchenDescription);
                appendIfDefined("dealTypeId", dealData.dealTypeId);
                appendIfDefined("originalPrice", dealData.originalPrice);
                appendIfDefined("dealPrice", dealData.dealPrice);
                appendIfDefined("totalQuantity", dealData.totalQuantity);
                appendBooleanIfDefined("isPublic", dealData.isPublic);
                appendBooleanIfDefined("isFree", dealData.isFree);
                appendBooleanIfDefined("isOngoing", dealData.isOngoing);
                appendIfDefined("fulfillmentType", dealData.fulfillmentType);

                // Add availableUntil only if not ongoing
                if (!dealData.isOngoing && dealData.availableUntil) {
                    // Format date to ISO string with time
                    const dateWithTime = new Date(dealData.availableUntil);
                    dateWithTime.setHours(23, 59, 59, 0);
                    formData.append("availableUntil", dateWithTime.toISOString());
                }

                // Add categoryIds as JSON array
                if (Array.isArray(dealData.categoryIds)) {
                    const normalizedCategoryIds = normalizeNumericIdArray(dealData.categoryIds);
                    formData.append("categoryIds", JSON.stringify(normalizedCategoryIds));
                }

                // Add dealTerms as JSON array
                if (Array.isArray(dealData.dealTerms)) {
                    formData.append("dealTerms", JSON.stringify(dealData.dealTerms));
                }

                // Always send a newly selected image file.
                if (dealData.image instanceof File) {
                    formData.append("image", dealData.image);
                }

                // Explicitly clear existing image when user removed it in edit flow.
                if (imageChanged && !dealData.image && dealData.existingImageUrl) {
                    formData.append("clearImage", "true");
                }

                // Add Open Collaboration fields (API field names)
                appendBooleanIfDefined("openCollaboration", dealData.isOpenCollab);
                if (dealData.isOpenCollab) {
                    appendIfDefined("collaborationOfferAmount", dealData.collabOfferAmount);
                    appendIfDefined("collaborationCommissionRate", dealData.collabCommissionRate);
                }

                // Redemption fields
                appendIfDefined("redemptionType", dealData.redemptionType);
                if (dealData.redemptionDays != null && dealData.redemptionDays !== "") {
                    formData.append("redemptionDays", dealData.redemptionDays);
                }
                if (dealData.redemptionDate) {
                    const rd = new Date(dealData.redemptionDate);
                    rd.setHours(23, 59, 59, 0);
                    formData.append("redemptionDate", rd.toISOString());
                }

                // Promotion: draft → active conversion uses status field
                if (dealData.status) formData.append("status", dealData.status);
            }

            // Per-user purchase limits (allowed on live deals too). Always sent so a
            // blanked field clears to null: "" → backend maps to null; a number sets
            // it; oneTimeOnly off → null. Skipped only when the field is absent from
            // the payload entirely (undefined), to avoid clobbering on partial edits.
            if (dealData.maxOrdersPerWindow !== undefined) {
                formData.append("maxOrdersPerWindow", dealData.maxOrdersPerWindow == null ? "" : dealData.maxOrdersPerWindow);
            }
            if (dealData.orderWindowDays !== undefined) {
                formData.append("orderWindowDays", dealData.orderWindowDays == null ? "" : dealData.orderWindowDays);
            }
            if (dealData.maxQtyPerOrder !== undefined) {
                formData.append("maxQtyPerOrder", dealData.maxQtyPerOrder == null ? "" : dealData.maxQtyPerOrder);
            }
            if (dealData.oneTimeOnly !== undefined) {
                formData.append("oneTimeOnly", dealData.oneTimeOnly ? "true" : "false");
            }

            // console.log("UPDATE DEAL PAYLOAD---->", {
            //     dealId: dealId,
            //     title: dealData.title,
            //     description: dealData.description,
            //     dealTypeId: dealData.dealTypeId,
            //     originalPrice: dealData.originalPrice,
            //     dealPrice: dealData.dealPrice,
            //     totalQuantity: dealData.totalQuantity,
            //     isPublic: dealData.isPublic,
            //     isFree: !!dealData.isFree,
            //     isOngoing: dealData.isOngoing,
            //     fulfillmentType: dealData.fulfillmentType,
            //     categoryIds: dealData.categoryIds,
            //     dealTerms: dealData.dealTerms,
            //     imageChanged: imageChanged,
            //     hasImage: dealData.image instanceof File,
            //     clearImage: imageChanged && !dealData.image && !!dealData.existingImageUrl,
            //     restrictToLockedFields,
            //     openCollaboration: dealData.isOpenCollab,
            //     collaborationOfferAmount: dealData.collabOfferAmount,
            //     collaborationCommissionRate: dealData.collabCommissionRate,
            // });

            const response = await postLoginService.put(url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            // console.log("UPDATE DEAL RESPONSE---->", response);

            if (response?.success) {
                // Success toast intentionally suppressed; UI handles silent redirect.
                // alert.success(response?.message || "Deal updated successfully!");
                return response;
            } else {
                alert.error(response?.message || "Failed to update deal");
                return rejectWithValue(response?.message || "Failed to update deal");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to update deal";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Create a new deal
export const createDeal = createAsyncThunk(
    "deals/createDeal",
    async (dealData, { rejectWithValue }) => {
        try {
            const url = `/api/merchants/deals`;

            // Create FormData for file upload
            const formData = new FormData();

            // Add text fields
            formData.append("title", dealData.title);
            formData.append("description", dealData.description);
            // Kitchen description is optional — only send it when the merchant
            // typed something, so an empty field defaults to the description.
            if (dealData.kitchenDescription && dealData.kitchenDescription.trim())
                formData.append("kitchenDescription", dealData.kitchenDescription);
            formData.append("dealTypeId", dealData.dealTypeId);
            formData.append("originalPrice", dealData.originalPrice);
            formData.append("dealPrice", dealData.dealPrice);
            formData.append("totalQuantity", dealData.totalQuantity);
            formData.append("isPublic", dealData.isPublic ? "true" : "false");
            formData.append("isFree", dealData.isFree ? "true" : "false");
            formData.append("isOngoing", dealData.isOngoing ? "true" : "false");
            formData.append("fulfillmentType", dealData.fulfillmentType);

            // Add availableUntil only if not ongoing
            if (!dealData.isOngoing && dealData.availableUntil) {
                // Format date to ISO string with time
                const dateWithTime = new Date(dealData.availableUntil);
                dateWithTime.setHours(23, 59, 59, 0);
                formData.append("availableUntil", dateWithTime.toISOString());
            }

            // Add categoryIds as JSON array
            const normalizedCategoryIds = normalizeNumericIdArray(dealData.categoryIds);
            formData.append("categoryIds", JSON.stringify(normalizedCategoryIds));

            // Add dealTerms as JSON array
            formData.append("dealTerms", JSON.stringify(dealData.dealTerms));

            // Add image file
            if (dealData.image instanceof File) {
                formData.append("image", dealData.image);
            }

            // Add Open Collaboration fields (API field names)
            formData.append("openCollaboration", dealData.isOpenCollab ? "true" : "false");
            if (dealData.isOpenCollab) {
                if (dealData.collabOfferAmount != null) {
                    formData.append("collaborationOfferAmount", dealData.collabOfferAmount);
                }
                if (dealData.collabCommissionRate != null) {
                    formData.append("collaborationCommissionRate", dealData.collabCommissionRate);
                }
            }

            // Redemption fields
            if (dealData.redemptionType) formData.append("redemptionType", dealData.redemptionType);
            if (dealData.redemptionDays != null && dealData.redemptionDays !== "") {
                formData.append("redemptionDays", dealData.redemptionDays);
            }
            if (dealData.redemptionDate) {
                const rd = new Date(dealData.redemptionDate);
                rd.setHours(23, 59, 59, 0);
                formData.append("redemptionDate", rd.toISOString());
            }

            // Per-user purchase limits (blank = no limit; backend maps "" → null).
            if (dealData.maxOrdersPerWindow !== undefined) {
                formData.append("maxOrdersPerWindow", dealData.maxOrdersPerWindow == null ? "" : dealData.maxOrdersPerWindow);
            }
            if (dealData.orderWindowDays !== undefined) {
                formData.append("orderWindowDays", dealData.orderWindowDays == null ? "" : dealData.orderWindowDays);
            }
            if (dealData.maxQtyPerOrder !== undefined) {
                formData.append("maxQtyPerOrder", dealData.maxQtyPerOrder == null ? "" : dealData.maxQtyPerOrder);
            }
            if (dealData.oneTimeOnly !== undefined) {
                formData.append("oneTimeOnly", dealData.oneTimeOnly ? "true" : "false");
            }

            // console.log("CREATE DEAL PAYLOAD---->", {
            //     title: dealData.title,
            //     description: dealData.description,
            //     dealTypeId: dealData.dealTypeId,
            //     originalPrice: dealData.originalPrice,
            //     dealPrice: dealData.dealPrice,
            //     totalQuantity: dealData.totalQuantity,
            //     isPublic: dealData.isPublic,
            //     isFree: !!dealData.isFree,
            //     isOngoing: dealData.isOngoing,
            //     fulfillmentType: dealData.fulfillmentType,
            //     categoryIds: dealData.categoryIds,
            //     dealTerms: dealData.dealTerms,
            //     hasImage: dealData.image instanceof File,
            //     openCollaboration: dealData.isOpenCollab,
            //     collaborationOfferAmount: dealData.collabOfferAmount,
            //     collaborationCommissionRate: dealData.collabCommissionRate,
            // });

            const response = await postLoginService.post(url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            // console.log("CREATE DEAL RESPONSE----> 321", response);

            if (response?.success) {
                // Success toast intentionally suppressed; UI handles silent redirect.
                // alert.success(response?.message || "Deal created successfully!");
                return response;
            } else {
                alert.error(response?.message || "Failed to create deal");
                return rejectWithValue(response?.message || "Failed to create deal");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to create deal";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// ---------- Draft thunks ----------

// Build FormData payload shared by draft + deal endpoints
const buildDealFormData = (dealData, { includeImage = true } = {}) => {
    const formData = new FormData();

    if (dealData.title != null) formData.append("title", dealData.title);
    if (dealData.description != null) formData.append("description", dealData.description);
    if (dealData.kitchenDescription && dealData.kitchenDescription.trim())
        formData.append("kitchenDescription", dealData.kitchenDescription);
    if (dealData.dealTypeId != null) formData.append("dealTypeId", dealData.dealTypeId);
    if (dealData.originalPrice != null && dealData.originalPrice !== "") {
        formData.append("originalPrice", dealData.originalPrice);
    }
    if (dealData.dealPrice != null && dealData.dealPrice !== "") {
        formData.append("dealPrice", dealData.dealPrice);
    }
    if (dealData.totalQuantity != null) formData.append("totalQuantity", dealData.totalQuantity);
    if (dealData.isPublic != null) formData.append("isPublic", dealData.isPublic ? "true" : "false");
    if (dealData.isFree != null) formData.append("isFree", dealData.isFree ? "true" : "false");
    if (dealData.isOngoing != null) formData.append("isOngoing", dealData.isOngoing ? "true" : "false");
    if (dealData.fulfillmentType) formData.append("fulfillmentType", dealData.fulfillmentType);

    if (!dealData.isOngoing && dealData.availableUntil) {
        const dateWithTime = new Date(dealData.availableUntil);
        dateWithTime.setHours(23, 59, 59, 0);
        formData.append("availableUntil", dateWithTime.toISOString());
    }

    if (Array.isArray(dealData.categoryIds)) {
        const normalizedCategoryIds = normalizeNumericIdArray(dealData.categoryIds);
        formData.append("categoryIds", JSON.stringify(normalizedCategoryIds));
    }
    if (Array.isArray(dealData.dealTerms)) {
        formData.append("dealTerms", JSON.stringify(dealData.dealTerms));
    }

    if (includeImage && dealData.image instanceof File) {
        formData.append("image", dealData.image);
    }

    if (dealData.isOpenCollab != null) {
        formData.append("openCollaboration", dealData.isOpenCollab ? "true" : "false");
        if (dealData.isOpenCollab) {
            if (dealData.collabOfferAmount != null) {
                formData.append("collaborationOfferAmount", dealData.collabOfferAmount);
            }
            if (dealData.collabCommissionRate != null) {
                formData.append("collaborationCommissionRate", dealData.collabCommissionRate);
            }
        }
    }

    // Redemption fields
    if (dealData.redemptionType) formData.append("redemptionType", dealData.redemptionType);
    if (dealData.redemptionDays != null && dealData.redemptionDays !== "") {
        formData.append("redemptionDays", dealData.redemptionDays);
    }
    if (dealData.redemptionDate) {
        const rd = new Date(dealData.redemptionDate);
        rd.setHours(23, 59, 59, 0);
        formData.append("redemptionDate", rd.toISOString());
    }

    // Per-user purchase limits (blank = no limit; backend maps "" → null).
    if (dealData.maxOrdersPerWindow !== undefined) {
        formData.append("maxOrdersPerWindow", dealData.maxOrdersPerWindow == null ? "" : dealData.maxOrdersPerWindow);
    }
    if (dealData.orderWindowDays !== undefined) {
        formData.append("orderWindowDays", dealData.orderWindowDays == null ? "" : dealData.orderWindowDays);
    }
    if (dealData.maxQtyPerOrder !== undefined) {
        formData.append("maxQtyPerOrder", dealData.maxQtyPerOrder == null ? "" : dealData.maxQtyPerOrder);
    }
    if (dealData.oneTimeOnly !== undefined) {
        formData.append("oneTimeOnly", dealData.oneTimeOnly ? "true" : "false");
    }

    if (dealData.status) formData.append("status", dealData.status);

    // clearImage flag — delete existing image on backend without uploading new one
    if (dealData.clearImage) formData.append("clearImage", "true");

    return formData;
};

export const createDealDraft = createAsyncThunk(
    "deals/createDealDraft",
    async (dealData, { rejectWithValue }) => {
        try {
            const url = `/api/merchants/deals/drafts`;
            const formData = buildDealFormData(dealData);
            const response = await postLoginService.post(url, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (response?.success) {
                return response.data;
            }
            return rejectWithValue(response?.message || "Failed to save draft");
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to save draft";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const updateDealDraft = createAsyncThunk(
    "deals/updateDealDraft",
    async ({ draftId, dealData }, { rejectWithValue }) => {
        try {
            const url = `/api/merchants/deals/drafts/${draftId}`;
            const formData = buildDealFormData(dealData);
            const response = await postLoginService.put(url, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (response?.success) {
                return response.data;
            }
            return rejectWithValue(response?.message || "Failed to update draft");
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to update draft";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getDealDraftById = createAsyncThunk(
    "deals/getDealDraftById",
    async (draftId, { rejectWithValue }) => {
        try {
            const url = `/api/merchants/deals/drafts/${draftId}`;
            const response = await postLoginService.get(url);
            if (response?.success) {
                return response.data;
            }
            return rejectWithValue(response?.message || "Failed to fetch draft");
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch draft";
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteDealDraft = createAsyncThunk(
    "deals/deleteDealDraft",
    async (draftId, { rejectWithValue }) => {
        try {
            const url = `/api/merchants/deals/drafts/${draftId}`;
            const response = await postLoginService.delete(url);
            if (response?.success) {
                return { draftId };
            }
            return rejectWithValue(response?.message || "Failed to delete draft");
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete draft";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Fetch merchant deals filtered by status (e.g. 'draft' for the drafts tab)
export const fetchMerchantDeals = createAsyncThunk(
    "deals/fetchMerchantDeals",
    async (params = {}, { rejectWithValue }) => {
        try {
            const queryString = new URLSearchParams(params).toString();
            const url = queryString
                ? `/api/merchants/me/deals?${queryString}`
                : `/api/merchants/me/deals`;
            const response = await postLoginService.get(url);
            if (response?.success) {
                return response.data;
            }
            return rejectWithValue(response?.message || "Failed to fetch deals");
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch deals";
            return rejectWithValue(errorMessage);
        }
    }
);

export const updateDealPauseStatus = createAsyncThunk(
    "deals/updateDealPauseStatus",
    async ({ dealId, isPaused }, { rejectWithValue }) => {
        try {
            const url = `/api/merchants/deals/${dealId}/pause`;
            const payload = { isPaused };
            const response = await postLoginService.patch(url, payload);

            if (response?.success) {
                return {
                    dealId,
                    isPaused,
                    message: response?.message,
                };
            }

            const message = response?.message || "Failed to update deal pause status";
            alert.error(message);
            return rejectWithValue(message);
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message || error?.message || "Failed to update deal pause status";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Get deal details by ID for merchant
export const getDealById = createAsyncThunk(
    "deals/getDealById",
    async (dealId, { rejectWithValue }) => {
        try {
            const url = `/api/merchants/deals/${dealId}`;
            const response = await postLoginService.get(url);
            // console.log("GET MERCHANT DEAL BY ID RESPONSE---->", response);

            if (response?.success) {
                return response.data;
            } else {
                alert.error(response?.message || "Failed to fetch deal details");
                return rejectWithValue(response?.message || "Failed to fetch deal details");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch deal details";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Get deal details by ID for influencer
export const fetchDealAnalytics = createAsyncThunk(
    "deals/fetchDealAnalytics",
    async ({ month, year }, { rejectWithValue }) => {
        try {
            const url = `/api/deal-analytics/merchant/dashboard?month=${month}&year=${year}`;
            const response = await postLoginService.get(url);
            // console.log("FETCH DEAL ANALYTICS RESPONSE---->", response);

            if (response?.success) {
                return response.data;
            } else {
                alert.error(response?.message || "Failed to fetch deal analytics");
                return rejectWithValue(response?.message || "Failed to fetch deal analytics");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch deal analytics";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getInfluencerDealById = createAsyncThunk(
    "deals/getInfluencerDealById",
    async (dealId, { rejectWithValue }) => {
        try {
            const url = `/api/influencers/deals/${dealId}`;
            const response = await postLoginService.get(url);
            // console.log("GET INFLUENCER DEAL BY ID RESPONSE---->", response);

            if (response?.success) {
                return response.data;
            } else {
                alert.error(response?.message || "Failed to fetch deal details");
                return rejectWithValue(response?.message || "Failed to fetch deal details");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch deal details";
            alert.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
